import React, { useEffect, useRef, useState } from 'react';
import webSocketService from '../services/websocket/WebSocketService';
import { getGameManager } from '@game/GameManager';

type Position = { x: number; y: number };

// Backend TerrainType enum mirror (string values used in MAP_GENERATED payload)
type Terrain = 'PLAIN' | 'WATER' | 'FOREST' | 'MOUNTAIN' | 'BUILDING';

const MICRO_W = 11;
const MICRO_H = 11;
const CENTER: Position = { x: Math.floor(MICRO_W / 2), y: Math.floor(MICRO_H / 2) }; // (5,5)

const SingleAgentMicroMapTest: React.FC = () => {
  const [socketUrl, setSocketUrl] = useState<string>(() => {
    try { return webSocketService.getConfig().url; } catch { return 'http://localhost:8080'; }
  });
  const [connected, setConnected] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);
  const [turn, setTurn] = useState<number>(0);
  const [seed, setSeed] = useState<string>('');
  const [agentId, setAgentId] = useState<string>('sa_agent1');
  const [mapReady, setMapReady] = useState<boolean>(false);
  const [agentRegistered, setAgentRegistered] = useState<boolean>(false);
  const [knownAgents, setKnownAgents] = useState<string[]>([]);
  const [inventories, setInventories] = useState<Record<string, { slots: Array<{ item:any; quantity:number }>; equipment: Record<string, any> }>>({});
  const [captureRunning, setCaptureRunning] = useState<boolean>(false);
  const [captureData, setCaptureData] = useState<any|null>(null);
  const [lastScores, setLastScores] = useState<Array<{ x:number; y:number; score:number }>>([]);
  const [decisionHL, setDecisionHL] = useState<any|null>(null);
  const [decisionTL, setDecisionTL] = useState<any|null>(null);
  const [decisionActions, setDecisionActions] = useState<any[]|null>(null);
  const [selectedScenario, setSelectedScenario] = useState<string>('');
  const [scenarioId, setScenarioId] = useState<string>('');
  const [heatmapReady, setHeatmapReady] = useState<boolean>(false);
  // Overlays: resources/enemies/safe zone
  const [overlayResources, setOverlayResources] = useState<Array<{ id:string; x:number; y:number; category:string; type?:string }>>([]);
  const [overlayEnemies, setOverlayEnemies] = useState<Array<{ id:string; x:number; y:number }>>([]);
  const [overlaySafeZone, setOverlaySafeZone] = useState<{ center?:{x:number;y:number}; radius?:number } | null>(null);
  const mapContainerRef = useRef<HTMLDivElement|null>(null);

  const microGridRef = useRef<Terrain[][] | null>(null);

  const log = (s: string) => setLogs(prev => [`[${new Date().toLocaleTimeString()}] ${s}`, ...prev].slice(0, 400));

  // Boot Phaser Game once
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const gm = getGameManager();
        await (gm as any).initialize?.({ connect: false });
        if (mounted) gm.start();
      } catch (e) { console.error('Phaser boot failed:', e); }
    })();

    // Listen to frontend map ready
    const onMapReady = () => { setMapReady(true); log('Map ready (frontend)'); };
    window.addEventListener('ABR_MAP_READY', onMapReady as any);

    // Bridge listeners (minimal)
    const onTurnStarted = (d:any) => { setTurn(d?.turnNumber||0); log(`TURN_STARTED ${d?.turnNumber}`); };
    const onSpawn = (d:any) => {
      const ag = d?.agent || d;
      if (ag?.id) {
        setKnownAgents(prev=> Array.from(new Set([...prev, String(ag.id)])));
        // 若是当前测试agent，则标记为已注册，解锁测试按钮
        try { if (String(ag.id) === String(agentId)) setAgentRegistered(true); } catch {}
      }
      log(`AGENT_SPAWNED ${ag?.id} @(${ag?.position?.x},${ag?.position?.y})`);
    };
    const onMoved = (d:any) => log(`AGENT_MOVED ${d?.agentId}→(${(d?.toPosition||d?.position)?.x},${(d?.toPosition||d?.position)?.y})`);
    const onResSpawn = (p:any) => {
      log('RESOURCE_SPAWNED');
      try {
        const items = Array.isArray(p?.newItems) ? p.newItems : [];
        const mapped = items
          .filter((it:any)=> it?.position && typeof it.position.x==='number' && typeof it.position.y==='number')
          .map((it:any)=>({ id: String(it?.itemId||it?.id||it?.resourceId||Math.random()), x: it.position.x, y: it.position.y, category: String(it?.category||'').toLowerCase(), type: String(it?.weaponType||it?.medicalType||it?.type||'') }));
        setOverlayResources(mapped);
      } catch {}
    };
    const onResCollected = (d:any) => {
      log('RESOURCE_COLLECTED');
      try {
        const id = String(d?.itemId||'');
        setOverlayResources(prev => prev.filter(r => r.id !== id));
      } catch {}
    };
    const onSafeUpdated = (d:any) => {
      try {
        const info = d?.safeZoneInfo || d?.safeZone || {};
        if (info?.center && typeof info?.currentRadius==='number') setOverlaySafeZone({ center: info.center, radius: info.currentRadius });
        else if (info?.center && typeof info?.radius==='number') setOverlaySafeZone({ center: info.center, radius: info.radius });
        log(`SAFEZONE_UPDATED r=${info?.currentRadius||info?.radius}`);
      } catch {}
    };
    const onSpawnOverlay = (d:any) => {
      const ag = d?.agent || d;
      if (ag?.id) {
        setKnownAgents(prev=> Array.from(new Set([...prev, String(ag.id)])));
        // 记录敌人（简化：color=red 或 type='enemy' 视为敌方）
        const isEnemy = String(ag?.type||'').toLowerCase()==='enemy' || String(ag?.color||'').toLowerCase()==='red';
        if (isEnemy && ag?.position) setOverlayEnemies(prev => prev.concat([{ id:String(ag.id), x:ag.position.x, y:ag.position.y }]));
        try { if (String(ag.id) === String(agentId)) setAgentRegistered(true); } catch {}
      }
      log(`AGENT_SPAWNED ${ag?.id} @(${ag?.position?.x},${ag?.position?.y})`);
    };
    const onMovedOverlay = (d:any) => {
      log(`AGENT_MOVED ${d?.agentId}→(${(d?.toPosition||d?.position)?.x},${(d?.toPosition||d?.position)?.y})`);
      try {
        const id = String(d?.agentId||''); const pos = d?.toPosition||d?.position;
        if (id && pos) setOverlayEnemies(prev => prev.map(e => e.id===id? { ...e, x:pos.x, y:pos.y }: e));
      } catch {}
    };

    webSocketService.onGameEvent('TURN_STARTED', onTurnStarted);
    webSocketService.onGameEvent('AGENT_SPAWNED', onSpawnOverlay);
    webSocketService.onGameEvent('AGENT_MOVED', onMovedOverlay);
    webSocketService.onGameEvent('RESOURCE_SPAWNED', onResSpawn);
    webSocketService.onGameEvent('RESOURCE_COLLECTED', onResCollected);
    webSocketService.onGameEvent('SAFEZONE_UPDATED', onSafeUpdated);

    // Listen for backend heatmap diagnostic mark (small-scale trial)
    const onHeatmapMark = (e: any) => {
      try {
        const cells = Array.isArray(e?.detail?.cells) ? e.detail.cells : [];
        if (cells.length > 0) {
          setHeatmapReady(true);
          log(`ui:heatmap received (Top=${cells.length})`);
        }
      } catch {}
    };
    window.addEventListener('ABR_UI_HEATMAP_MARK', onHeatmapMark as any);

    // Note: mock local map generator removed; backend authoritative map is used

    return () => {
      try { window.removeEventListener('ABR_MAP_READY', onMapReady as any); } catch {}
      webSocketService.off('TURN_STARTED', onTurnStarted);
      webSocketService.off('AGENT_SPAWNED', onSpawnOverlay);
      webSocketService.off('AGENT_MOVED', onMovedOverlay);
      webSocketService.off('RESOURCE_SPAWNED', onResSpawn);
      webSocketService.off('RESOURCE_COLLECTED', onResCollected);
      webSocketService.off('SAFEZONE_UPDATED', onSafeUpdated);
      try { window.removeEventListener('ABR_UI_HEATMAP_MARK', onHeatmapMark as any); } catch {}
    };
  }, []);

  // Keep connection indicator fresh
  useEffect(() => {
    const id = setInterval(() => {
      try { setConnected(webSocketService.isConnected()); } catch {}
    }, 500);
    return () => clearInterval(id);
  }, []);

  const connect = async () => {
    try { await webSocketService.connect(socketUrl); setConnected(true); log(`Connected ${socketUrl}`); }
    catch(e:any){ setConnected(false); log(`Connect failed: ${e?.message||e}`); }
  };

  // Control helpers
  const prepare = async () => { const r:any=await webSocketService.emit('game:prepare'); log(`Prepare: ${r?.success?'ok':'fail'}`); };
  const start    = async () => { await webSocketService.emit('game:setSpeed', { intervalMs: 30000 }); const r:any=await webSocketService.emit('game:start'); log(`Start: ${r?.success?'ok':'fail'}`); };
  const stop     = async () => { const r:any=await webSocketService.emit('game:stop'); log(`Stop: ${r?.success?'ok':'fail'}`); };
  const step     = async () => { const r:any=await webSocketService.emit('game:step'); log(`Step → turn ${r?.turn ?? '?'} ${r?.success?'ok':'fail'}`); };
  const useMock  = async () => { const r:any=await webSocketService.emit('sds:mock', { enabled: true }); log(`Mock SDS: ${r?.success?'enabled':'fail'}`); };

  // Build 11x11 micro-map from backend authoritative map snapshot; also apply mask and disable auto resources
  const buildMicroMap = async () => {
    try {
      const snap:any = await webSocketService.emit('dev:map:snapshot');
      if (!snap?.success) { log(`map snapshot failed: ${snap?.error||'unknown'}`); return; }
      const full: string[][] = snap.grid;
      if (!full || !Array.isArray(full) || full.length === 0) { log('invalid map snapshot'); return; }
      // Slice centered 11x11 window for micro view (more representative sample)
      const H = full.length;
      const W = full[0]?.length || 0;
      const startY = Math.max(0, Math.floor((H - MICRO_H) / 2));
      const startX = Math.max(0, Math.floor((W - MICRO_W) / 2));
      const grid: Terrain[][] = [] as any;
      for (let y=0; y<MICRO_H; y++) {
        const srcRow = full[startY + y] || [];
        const row = (srcRow.slice(startX, startX + MICRO_W) as Terrain[]);
        while (row.length < MICRO_W) row.push('PLAIN');
        grid.push(row);
      }
      // 1) Frontend render via simulated MAP_GENERATED using backend map
      webSocketService.triggerGameEvent('MAP_GENERATED', { grid });
      microGridRef.current = grid;
      log(`MAP_GENERATED dispatched from backend snapshot (${MICRO_W}x${MICRO_H})`);

      // 2) Backend resource mask for 11x11 starting at (0,0)
      const allowed: Position[] = [];
      for (let y=0;y<MICRO_H;y++) for (let x=0;x<MICRO_W;x++) allowed.push({ x, y });
      try {
        await webSocketService.emit('map:mask', { allowed });
        // 禁用后端首回合的自动资源生成，避免11x11被铺满
        await webSocketService.emit('dev:resources:configure', { disable: true });
        log('map:mask applied and auto resource generation disabled');
      } catch (e:any) { log(`map:mask/config failed: ${e?.message||e}`); }

      setMapReady(true);
    } catch (e:any) {
      log(`Micro map build failed: ${e?.message||e}`);
    }
  };

  // ===== TestEngine scenario runner =====

  async function applyScenario(name: string, grid: string[][]) {
    if (!connected) await connect();
    setLastScores([]);
    setDecisionHL(null); setDecisionTL(null); setDecisionActions(null);
    try {
      log(`Scenario: ${name} → init test engine`);
      await webSocketService.emit('test:engine:init', { width: MICRO_W, height: MICRO_H });
      await webSocketService.emit('test:map:setGrid', { grid });
      webSocketService.triggerGameEvent('MAP_GENERATED', { grid });
      microGridRef.current = grid as any;
      // mask all 11x11
      const allowed: Position[] = []; for (let y=0;y<MICRO_H;y++) for (let x=0;x<MICRO_W;x++) allowed.push({ x, y });
      await webSocketService.emit('test:map:mask', { allowed });
      await webSocketService.emit('test:resources:configure', { disable: true });
      setSelectedScenario(name);
      setCaptureData(null);
      log(`Scenario ${name} applied. Next: Generate Resources, then Start Decision.`);
      setMapReady(true);
    } catch (e:any) { log(`Scenario failed: ${e?.message||e}`); }
  }

  async function generateResourcesButton() {
    try {
      if (!selectedScenario) { log('Select a scenario first.'); return; }
      const key = scenarioKeyFromName(selectedScenario);
      const items = resourcesForScenario(key);
      const ack:any = await webSocketService.emit('test:resource:spawn', { items, currentTurn: 0 });
      log(`Resources spawned: ${ack?.items?.length||0}`);
    } catch (e:any) { log(`generateResources failed: ${e?.message||e}`); }
  }

  async function startDecisionButton() {
    try {
      // 直接触发一次完整决策（场景已通过 CSV 装配）
      const res:any = await webSocketService.emit('test:decision:run', { agentId });
      if (!res?.success) { log(`decision failed: ${res?.error||'unknown'}`); return; }
      setDecisionHL(res.result?.HL || null);
      setDecisionTL(res.result?.TL || null);
      setDecisionActions(res.result?.actions || null);
      // 同步拉取评分快照/热力图
      const snap:any = await webSocketService.emit('test:analysis:snapshot', { agentId });
      const scoreMap = snap?.result?.tileScores || {};
      const entries: Array<{ x:number;y:number;score:number }> = Object.values(scoreMap).map((v:any)=>({ x:v.position.x, y:v.position.y, score: (v.scores?.[0]?.total)||0 }));
      entries.sort((a,b)=>b.score-a.score);
      setLastScores(entries.slice(0,10));
      setCaptureData({ decision: res?.result, snapshot: snap?.result });
      try {
        // 请求服务端生成热力图（可视化）
        await webSocketService.emit('test:heatmap', { agentId });
        window.dispatchEvent(new CustomEvent('ABR_HEATMAP', { detail: { cells: entries.slice(0,10) } }));
      } catch {}
      log(`Decision complete. Actions=${(res?.result?.actions?.length||0)}`);
    } catch (e:any) { log(`startDecision failed: ${e?.message||e}`); }
  }

  async function executeActionsButton() {
    try {
      // Normalize actions; if empty, try to derive a MOVE from best tile
      let actions: any[] = Array.isArray(decisionActions) ? [...decisionActions] : [];
      if (actions.length === 0) {
        const top = lastScores[0];
        if (top && typeof top.x==='number' && typeof top.y==='number') {
          actions = [{ type:'MOVE', targetPosition: { x: top.x, y: top.y } }];
          log(`No actions from LLM; fallback MOVE to top scored tile (${top.x},${top.y})`);
        } else {
          log('No actions to execute. Run Start Decision first.');
          return;
        }
      }
      log(`Executing ${actions.length} action(s)...`);
      for (const act of actions) {
        const type = String(act.type||act.actionType||'').toUpperCase() || 'MOVE';
        const params: any = {};
        const tp = act.targetPosition || act.target || act.toPosition || act.position;
        if (tp && typeof tp.x==='number' && typeof tp.y==='number') params.targetPosition = { x: tp.x, y: tp.y };
        const ack:any = await webSocketService.emit('test:action', { agentId, type, params });
        log(`execute ${type}${params.targetPosition?`@(${params.targetPosition.x},${params.targetPosition.y})`:''} → ${ack?.success?'ok':'fail'}`);
        await new Promise(r=>setTimeout(r, 150));
      }
    } catch (e:any) { log(`executeActions failed: ${e?.message||e}`); }
  }

  // Build 8 scenarios
  const G = (fill: string = 'PLAIN') => Array.from({ length: MICRO_H }, () => Array(MICRO_W).fill(fill));
  const deepClone = (m:string[][])=> m.map(r=>r.slice());

  // 1) 全山环绕只留1口
  function gridMountainRingOneGate(): string[][] {
    const g = G();
    const min = 2, max = MICRO_W - 3; // inner ring border at 2..8 for 11x11
    for (let x=min;x<=max;x++){ g[min][x]='MOUNTAIN'; g[max][x]='MOUNTAIN'; }
    for (let y=min;y<=max;y++){ g[y][min]='MOUNTAIN'; g[y][max]='MOUNTAIN'; }
    // gate at left inner ring
    g[Math.floor(MICRO_H/2)][min]='PLAIN';
    return g;
  }
  // 2) 河流经过（横向），留两处桥位
  function gridRiverWithBridges(): string[][] {
    const g = G();
    const ry = 5;
    for (let x=0;x<MICRO_W;x++) g[ry][x] = 'WATER';
    // 保证中心(5,5)不被水覆盖：在桥位上包含中心
    g[ry][3] = 'PLAIN';
    g[ry][5] = 'PLAIN';
    g[ry][7] = 'PLAIN';
    return g;
  }
  // 3) 一片树林（右上区域）
  function gridForestPatch(): string[][] {
    const g = G(); for (let y=0;y<5;y++) for (let x=6;x<MICRO_W;x++) g[y][x]='FOREST'; return g;
  }
  // 4) 旷野上有一处building（中心偏右）
  function gridSingleBuilding(): string[][] { const g=G(); g[5][7]='BUILDING'; return g; }
  // 5) 之字峡谷（山形成的Z字）
  function gridZigZagCanyon(): string[][] {
    const g=G();
    for (let y=1;y<MICRO_H-1;y++) g[y][2] = 'MOUNTAIN';
    for (let y=1;y<MICRO_H-1;y++) g[y][8] = 'MOUNTAIN';
    for (let x=2;x<=8;x++) g[3][x] = 'MOUNTAIN';
    for (let x=2;x<=8;x++) g[7][x] = 'MOUNTAIN';
    // carve openings
    g[3][4]='PLAIN'; g[7][6]='PLAIN';
    return g;
  }
  // 6) 湖泊+地峡
  function gridLakeWithIsthmus(): string[][] {
    const g = G();
    // 湖泊区域
    for (let y=2;y<=6;y++) for (let x=2;x<=6;x++) g[y][x] = 'WATER';
    // 地峡改至中心列 x=5，确保 (5,5) 为可通行
    for (let y=2;y<=6;y++) g[y][5] = 'PLAIN';
    // 兜底：中心位置强制为PLAIN（若后续改动范围不一致）
    g[5][5] = 'PLAIN';
    return g;
  }
  // 7) 多路径等距（通往目标的多条路径）
  function gridMultiPath(): string[][] {
    const g=G();
    // add scattered mountains to create alternatives
    g[2][3]='MOUNTAIN'; g[3][3]='MOUNTAIN'; g[7][3]='MOUNTAIN'; g[6][6]='MOUNTAIN';
    return g;
  }
  // 8) 口袋地形（两侧封闭，中面对位）
  function gridPocket(): string[][] {
    const g=G();
    for (let y=2;y<=8;y++) { g[y][3]='MOUNTAIN'; g[y][7]='MOUNTAIN'; }
    g[5][3]='PLAIN'; g[5][7]='PLAIN'; // small entries
    return g;
  }

  // Resource presets per scenario (relative to CENTER)
  function resourcesForScenario(key: string): Array<{ type:string; position: Position; metadata?: any }> {
    const items: Array<{ type:string; position: Position; metadata?: any }> = [];
    const P = (x:number,y:number)=>({ x, y });
    switch (key) {
      case 'ring':
        items.push({ type:'WEAPON', position:P(3,5), metadata:{ weaponType:'AXE' } });
        items.push({ type:'MEDICAL', position:P(5,3), metadata:{ medicalType:'large' } });
        break;
      case 'river':
        items.push({ type:'WEAPON', position:P(3,4), metadata:{ weaponType:'SWORD' } });
        items.push({ type:'MEDICAL', position:P(7,6), metadata:{ medicalType:'small' } });
        break;
      case 'forest':
        items.push({ type:'WEAPON', position:P(8,2), metadata:{ weaponType:'AXE' } });
        items.push({ type:'MEDICAL', position:P(6,4), metadata:{ medicalType:'large' } });
        break;
      case 'building':
        items.push({ type:'WEAPON', position:P(7,5), metadata:{ weaponType:'SWORD' } });
        items.push({ type:'MEDICAL', position:P(4,4), metadata:{ medicalType:'small' } });
        break;
      case 'zigzag':
        items.push({ type:'WEAPON', position:P(4,2), metadata:{ weaponType:'AXE' } });
        items.push({ type:'MEDICAL', position:P(6,8), metadata:{ medicalType:'large' } });
        break;
      case 'lake':
        items.push({ type:'WEAPON', position:P(1,4), metadata:{ weaponType:'SWORD' } });
        items.push({ type:'MEDICAL', position:P(9,6), metadata:{ medicalType:'small' } });
        break;
      case 'multipath':
        items.push({ type:'WEAPON', position:P(2,2), metadata:{ weaponType:'AXE' } });
        items.push({ type:'MEDICAL', position:P(8,8), metadata:{ medicalType:'large' } });
        break;
      case 'pocket':
        items.push({ type:'WEAPON', position:P(5,2), metadata:{ weaponType:'SWORD' } });
        items.push({ type:'MEDICAL', position:P(5,8), metadata:{ medicalType:'small' } });
        break;
    }
    return items;
  }

  function scenarioKeyFromName(name: string): string {
    const map: Record<string,string> = {
      'Ring (one gate)': 'ring', 'Ring (1 gate)': 'ring',
      'River (bridges)': 'river', 'Forest Patch': 'forest', 'Single Building': 'building',
      'ZigZag Canyon': 'zigzag', 'Lake + Isthmus': 'lake', 'Multi-Path': 'multipath', 'Pocket': 'pocket'
    };
    return map[name] || 'ring';
  }

  // Register a single agent at center (5,5)
  const registerSingleAgent = async () => {
    try {
      const payload:any = {
        agentId,
        position: { x: CENTER.x, y: CENTER.y },
        agentType: 'agent',
        color: 'white',
        attributes: { health: 45, strength: 35, defense: 35, speed: 35 }
      };
      const ack:any = await webSocketService.emit('dev:agent:register', payload);
      if (ack?.success) { setAgentRegistered(true); log(`register ${agentId} → ok`); }
      else { log(`register ${agentId} → fail: ${ack?.error||'unknown'}`); }
    } catch (e:any) { log(`register error: ${e?.message||e}`); }
  };

  // Spawn random resources within 11x11 (avoid center, try to avoid impassables)
  const spawnMicroResources = async () => {
    try {
      const grid = microGridRef.current;
      const rnd = (min:number,max:number)=> Math.floor(Math.random()*(max-min+1))+min;
      const isPassable = (x:number,y:number)=>{ if (!grid) return true; const t = grid[y][x]; return t !== 'WATER' && t !== 'MOUNTAIN'; };
      const positions: Position[] = [];
      let tries = 0;
      while (positions.length < 6 && tries < 200) {
        tries++;
        const x = rnd(0, MICRO_W-1), y = rnd(0, MICRO_H-1);
        if ((x===CENTER.x && y===CENTER.y)) continue;
        if (!isPassable(x,y)) continue;
        if (positions.some(p=>p.x===x&&p.y===y)) continue;
        positions.push({ x, y });
      }
      const types = ['WEAPON','MEDICAL','MEDICAL','WEAPON','MEDICAL','WEAPON'];
      const wTypes = ['SWORD','AXE','LANCE'];
      const mTypes = ['large','small'];
      const items = positions.map((p, i) => {
        const t = types[i%types.length];
        if (t === 'WEAPON') return { type: t, position: p, metadata: { weaponType: wTypes[i%wTypes.length] } };
        else return { type: t, position: p, metadata: { medicalType: mTypes[i%mTypes.length] } };
      });
      const ack:any = await webSocketService.emit('dev:resource:spawn', { items, currentTurn: 0 });
      log(`spawn resources → ${ack?.success?'ok':'fail'} (${ack?.items?.length||0})`);
    } catch (e:any) { log(`spawn failed: ${e?.message||e}`); }
  };

  // Simple move script: move 1 step right then left using dev:map:moveAgent (pathing bypass; for animation check)
  const demoMove = async () => {
    try {
      const to1 = { x: CENTER.x + 1, y: CENTER.y };
      const to2 = { x: CENTER.x, y: CENTER.y };
      let ack:any = await webSocketService.emit('dev:map:moveAgent', { agentId, toPosition: to1 });
      log(`move → (${to1.x},${to1.y}) ${ack?.success?'ok':'fail'}`);
      ack = await webSocketService.emit('dev:map:moveAgent', { agentId, toPosition: to2 });
      log(`move → (${to2.x},${to2.y}) ${ack?.success?'ok':'fail'}`);
    } catch (e:any) { log(`move failed: ${e?.message||e}`); }
  };

  // ===== Single-turn capture for sa_agent1 =====
  async function runSingleTurnCapture(targetId: string) {
    if (!connected) { log('Not connected'); return; }
    setCaptureRunning(true); setCaptureData(null);
    const events: Array<{ t:number; type:string; data:any }> = [];
    const t0 = Date.now();
    const rec = (type:string)=>(d:any)=>{ try { events.push({ t: Date.now(), type, data: d }); } catch {} };

    try {
      // 1) 设置仅执行目标agent
      const ack1:any = await webSocketService.emit('dev:turn:target', { agentId: targetId });
      log(`target=${targetId} ${ack1?.success?'ok':'fail'}`);
      // 2) 前置快照
      const pre:any = await webSocketService.emit('dev:analysis:snapshot', { agentId: targetId });
      log(`pre-snapshot ${pre?.success?'ok':'fail'}`);

      // 3) 绑定临时事件监听（只记录，不驱动动画）
      // 🔧 移除冗余监听：只监听非动画事件，避免与Engine/CombatRouter冲突
      const types = [
        'AGENT_TURN_STARTED','AGENT_TURN_ENDED','TURN_STARTED','TURN_ENDED',
        'RESOURCE_SPAWNED','WEAPON_BROKEN',
        'SAFEZONE_UPDATED','SAFEZONE_WARNING','SAFEZONE_CONTRACTION_STARTED','SAFEZONE_CONTRACTION_COMPLETED'
        // ❌ 移除: AGENT_MOVED, RESOURCE_COLLECTED, WEAPON_EQUIPPED - 由Engine统一处理
        // ❌ 移除: COMBAT_* - 由CombatEventRouter统一处理
      ];
      const handlers: Record<string, any> = {};
      types.forEach(tp => { handlers[tp] = rec(tp); webSocketService.onGameEvent(tp, handlers[tp]); });

      // 4) 推进一回合
      const stepAck:any = await webSocketService.emit('game:step');
      log(`Step → ${stepAck?.success?'ok':''} turn=${stepAck?.turn||'?'} ${stepAck?.error||''}`);

      // 5) 等待 sa_agent1 的 AGENT_TURN_ENDED（超时90秒）
      await new Promise<void>((resolve, reject) => {
        const tid = setTimeout(()=>reject(new Error('capture timeout')), 90000);
        const done = (d:any)=>{
          if (d?.agentId === targetId) {
            clearTimeout(tid);
      try { webSocketService.off('AGENT_TURN_ENDED', handlers['AGENT_TURN_ENDED']); } catch {}
            resolve();
          }
        };
        // 单独监听以便快速完成
        webSocketService.onGameEvent('AGENT_TURN_ENDED', done);
      });

      // 6) 后置快照
      const post:any = await webSocketService.emit('dev:analysis:snapshot', { agentId: targetId });
      log(`post-snapshot ${post?.success?'ok':'fail'}`);

      // 7) 组装一次完整日志
      const full = {
        agentId: targetId,
        startedAt: new Date(t0).toISOString(),
        endedAt: new Date().toISOString(),
        durationMs: Date.now()-t0,
        preSnapshot: pre?.result||null,
        postSnapshot: post?.result||null,
        events
      };
      setCaptureData(full);
      log(`capture ready: ${events.length} events`);

      // 8) 可选：清除target（不自动清除，交由用户控制）
    } catch (e:any) {
      log(`capture failed: ${e?.message||e}`);
    } finally {
      setCaptureRunning(false);
    }
  }

  function downloadCapture() {
    try {
      const blob = new Blob([JSON.stringify(captureData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `single_turn_${captureData?.agentId || 'agent'}_${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {}
  }

  // Spawn threat enemies
  const spawnThreatEnemies = async () => {
    try {
      const highId = 'enemy_high';
      const lowId  = 'enemy_low';
      // 遵守属性总点数上限 150
      const rh:any = await webSocketService.emit('dev:agent:register', {
        agentId: highId, position: { x: CENTER.x+2, y: CENTER.y }, agentType: 'agent', color: 'red',
        attributes: { health: 80, strength: 40, defense: 20, speed: 10 } // sum=150
      });
      const rl:any = await webSocketService.emit('dev:agent:register', {
        agentId: lowId, position: { x: CENTER.x-2, y: CENTER.y }, agentType: 'agent', color: 'orange',
        attributes: { health: 10, strength: 10, defense: 10, speed: 20 } // sum=50
      });
      log(`register enemies → high:${rh?.success?'ok':'fail'} low:${rl?.success?'ok':'fail'}`);

      // 仅对注册成功的敌人做后续设置
      if (rh?.success) {
        await webSocketService.emit('dev:agent:setHealth', { agentId: highId, current: 100, max: 100 });
        const axe:any = await webSocketService.emit('dev:inventory:addItem', { agentId: highId, templateKey: 'axe_basic', overrides: { weaponType:'AXE', damage: 10 }, quantity: 1 });
        if (axe?.success && axe.item?.itemId) {
          await webSocketService.emit('dev:inventory:equip', { agentId: highId, itemId: axe.item.itemId });
        }
      }
      if (rl?.success) {
        await webSocketService.emit('dev:agent:setHealth', { agentId: lowId, current: 10, max: 100 });
        for (let i=0;i<3;i++) {
          await webSocketService.emit('dev:inventory:addItem', { agentId: lowId, templateKey: 'health_potion', overrides: { effect: { healthRestore: 30 } }, quantity: 1 });
        }
      }
      const idsToAdd = [rh?.success?highId:null, rl?.success?lowId:null].filter(Boolean) as string[];
      setKnownAgents(prev => Array.from(new Set([...prev, ...idsToAdd])));
      log('Threat enemies spawned (with inventories).');
      // 自动刷新一次背包视图
      await refreshInventories();
    } catch (e:any) { log(`spawn enemies failed: ${e?.message||e}`); }
  };


  const refreshInventories = async () => {
    try {
      const ids = Array.from(new Set([agentId, ...knownAgents])).filter(Boolean);
      if (ids.length === 0) { log('No agents to query'); return; }
      const ack:any = await webSocketService.emit('agents:getInventory', { ids });
      if (ack?.success) {
        const map: Record<string, { slots: Array<{ item:any; quantity:number }>; equipment: Record<string, any> }> = {};
        (ack.agents || []).forEach((row:any) => {
          const inv = row.inventory || { slots: [], equipment: {} };
          map[row.agentId] = { slots: inv.slots || [], equipment: inv.equipment || {} };
        });
        setInventories(map);
        log(`Inventories refreshed (${ids.length} agents)`);
      } else { log(`getInventory failed: ${ack?.error||'unknown'}`); }
    } catch (e:any) { log(`getInventory error: ${e?.message||e}`); }
  };

  // 临时测试：触发一次“持SWORD的slime移动（MOVE）”的分层动画（在BattleScene里播放）
  const testHeldSwordMove = () => {
    try {
      const detail = { agentId, weaponType: 'SWORD' };
      window.dispatchEvent(new CustomEvent('ABR_TEST_PLAY_LAYERED_MOVE', { detail }));
      log('Layered MOVE test dispatched (SWORD)');
    } catch (e:any) { log(`layered move test failed: ${e?.message||e}`); }
  };

  return (
    <div style={{ padding: 12, fontFamily: 'sans-serif' }}>
      <h2 style={{ margin:'6px 0 12px' }}>Single-Agent Micro Map Test (11x11)</h2>
      <div style={{ display:'flex', gap:12 }}>
        {/* Left control panel */}
        <div style={{ width: 360, minWidth: 320 }}>
          <div style={{ display:'grid', gap:8, marginBottom:12 }}>
            <input value={socketUrl} onChange={e=>setSocketUrl(e.target.value)} style={{ padding:6, fontFamily:'monospace' }} />
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={connect} style={{ flex:1 }}>Connect</button>
              <button onClick={useMock} style={{ flex:1 }}>Use Mock SDS</button>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={prepare} disabled={!connected} style={{ flex:1, opacity: (!connected)?0.6:1 }}>Prepare</button>
              <button onClick={start} disabled={!connected} style={{ flex:1, opacity: (!connected)?0.6:1 }}>Start (auto)</button>
              <button onClick={stop} style={{ flex:1 }}>Stop</button>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={step} disabled={!connected} style={{ flex:1, opacity: (!connected)?0.6:1 }}>Step 1 Turn</button>
            </div>
          </div>

          <div style={{ display:'grid', gap:8, marginBottom:12 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Test Scenarios</div>
            <div style={{ display:'grid', gap:8, gridTemplateColumns:'repeat(2, 1fr)' }}>
              <button onClick={()=>applyScenario('Ring (one gate)', gridMountainRingOneGate())}>Ring (1 gate)</button>
              <button onClick={()=>applyScenario('River (bridges)', gridRiverWithBridges())}>River (bridges)</button>
              <button onClick={()=>applyScenario('Forest Patch', gridForestPatch())}>Forest Patch</button>
              <button onClick={()=>applyScenario('Single Building', gridSingleBuilding())}>Building</button>
              <button onClick={()=>applyScenario('ZigZag Canyon', gridZigZagCanyon())}>ZigZag Canyon</button>
              <button onClick={()=>applyScenario('Lake + Isthmus', gridLakeWithIsthmus())}>Lake + Isthmus</button>
              <button onClick={()=>applyScenario('Multi-Path', gridMultiPath())}>Multi-Path</button>
              <button onClick={()=>applyScenario('Pocket', gridPocket())}>Pocket</button>
            </div>

            <div style={{ display:'flex', gap:8 }}>
              <button onClick={generateResourcesButton} style={{ flex:1 }}>Generate Resources</button>
              <button onClick={startDecisionButton} style={{ flex:1 }}>Start Decision</button>
              <button onClick={executeActionsButton} style={{ flex:1 }}>Execute Actions</button>
              <button onClick={() => {
                try {
                  const cells = [{ x: CENTER.x, y: CENTER.y, score: 1 }];
                  window.dispatchEvent(new CustomEvent('ABR_UI_HEATMAP_MARK', { detail: { cells, agentId } }));
                  log('Manual placeholder: dispatched ABR_UI_HEATMAP_MARK');
                  setHeatmapReady(true);
                } catch (e:any) { log(`Manual placeholder failed: ${e?.message||e}`); }
              }} style={{ flex:1 }}>Draw Placeholder</button>
            </div>

            {/* CSV批量场景（单个）运行入口 */}
            <div style={{ display:'flex', gap:8 }}>
              <input value={scenarioId} onChange={e=>setScenarioId(e.target.value)} placeholder="CSV 场景序号(1..30)" style={{ flex:1, padding:6, fontFamily:'monospace' }} />
              <button onClick={async()=>{
                try {
                  const id = Number(scenarioId||'');
                  if (!id || id<1) { log('请输入有效的场景序号'); return; }
                  const ack:any = await webSocketService.emit('test:scenario:run', { id });
                  if (ack?.success) log(`Scenario ${id} run OK`); else log(`Scenario ${id} run failed: ${ack?.error||'unknown'}`);
                } catch (e:any) { log(`scenario:run error: ${e?.message||e}`); }
              }} style={{ flex:1 }}>Run Scenario (CSV)</button>
            </div>

            <div style={{ display:'flex', gap:8 }}>
              <input value={seed} onChange={e=>setSeed(e.target.value)} placeholder="seed (optional)" style={{ flex:1, padding:6, fontFamily:'monospace' }} />
              <button onClick={buildMicroMap} style={{ flex:1 }}>Build 11x11 Map</button>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <input value={agentId} onChange={e=>setAgentId(e.target.value)} placeholder="agent id" style={{ flex:1, padding:6, fontFamily:'monospace' }} />
              <button onClick={registerSingleAgent} disabled={!mapReady} style={{ flex:1, opacity: (!mapReady)?0.6:1 }}>Register Agent @ center</button>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={async()=>{ await webSocketService.emit('dev:turn:target', { agentId: 'sa_agent1' }); }} style={{ flex:1 }}>TEST: Target sa_agent1 only</button>
              <button onClick={async()=>{ await webSocketService.emit('dev:turn:target', { agentId: '' }); }} style={{ flex:1 }}>TEST: Clear target</button>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={async()=>{ await runSingleTurnCapture('sa_agent1'); }} disabled={captureRunning} style={{ flex:1, opacity: captureRunning?0.6:1 }}>TEST: Capture 1 Turn (sa_agent1)</button>
              {captureData && (
                <button onClick={()=>downloadCapture()} style={{ flex:1 }}>Download JSON</button>
              )}
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={spawnMicroResources} disabled={!mapReady || !agentRegistered} style={{ flex:1, opacity: (!mapReady||!agentRegistered)?0.6:1 }}>Spawn Resources (random in 11x11)</button>
              <button onClick={demoMove} disabled={!agentRegistered} style={{ flex:1, opacity: (!agentRegistered)?0.6:1 }}>Demo Move</button>
              <button onClick={testHeldSwordMove} disabled={!agentRegistered} style={{ flex:1, opacity: (!agentRegistered)?0.6:1 }}>Test Sword Held MOVE</button>
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={async()=>{ await spawnThreatEnemies(); }} disabled={!mapReady} style={{ flex:1, opacity: (!mapReady)?0.6:1 }}>Spawn Threat Enemies</button>
              <button onClick={refreshInventories} style={{ flex:1 }}>Refresh Inventories</button>
            </div>
          </div>

          <div style={{ fontSize:12, marginBottom:8 }}>Current Turn: {turn}</div>
          <div style={{ marginBottom:8, fontSize:12 }}>
            <div>Map: 11x11, agent at (5,5)</div>
            <div>Connected: {String(connected)} | MapReady: {String(mapReady)} | Agent: {String(agentRegistered)}</div>
          </div>
        </div>

        {/* Right map + logs */}
        <div style={{ flex:1, minWidth: 640 }}>
          <div ref={mapContainerRef} style={{ border:'1px solid #333', height: 560, position:'relative', marginBottom:12 }}>
            <div id="phaser-game" style={{ position:'absolute', inset:0, zIndex: 1 }} />
            {/* Heatmap overlay (no external axes) */}
            <div style={{ position:'absolute', left:0, top:0, right:0, bottom:0, pointerEvents:'none', zIndex: 5 }}>
              {/* Small badge when backend ui:heatmap arrives */}
              {heatmapReady && (
                <div style={{ position:'absolute', left:8, top:8, background:'#10b981', color:'#fff', fontSize:12, padding:'2px 6px', borderRadius:4, boxShadow:'0 1px 2px rgba(0,0,0,0.2)' }}>
                  Heatmap ✓
                </div>
              )}
              {/* Heatmap top-10 */}
              {lastScores.map((s, i) => {
                const intensity = 1 - i / 10; // 1..0
                const r = Math.round(255 * intensity);
                const g = Math.round(80 * intensity);
                const b = 0;
                return (
                  <div key={`hm-${i}`} style={{
                    position:'absolute',
                    left: `${(s.x)*100/MICRO_W}%`,
                    top: `${(s.y)*100/MICRO_H}%`,
                    width: `${100/MICRO_W}%`,
                    height: `${100/MICRO_H}%`,
                    background: `rgba(${r},${g},${b},0.35)`,
                    outline: '1px solid rgba(255,255,255,0.15)'
                  }} title={`(${s.x},${s.y}) score=${s.score.toFixed(2)}`} />
                );
              })}
              {/* Resource overlays */}
              {overlayResources.map((r,i)=>{
                const color = r.category==='weapon' ? 'rgba(255,165,0,0.85)' : 'rgba(16,185,129,0.85)';
                return (
                  <div key={`res-${r.id}-${i}`} style={{
                    position:'absolute',
                    left: `${(r.x)*100/MICRO_W}%`,
                    top: `${(r.y)*100/MICRO_H}%`,
                    width: `${100/MICRO_W}%`,
                    height: `${100/MICRO_H}%`,
                    background: color,
                    outline: '1px solid rgba(255,255,255,0.2)'
                  }} title={`RES ${r.category}:${r.type} @(${r.x},${r.y})`} />
                );
              })}
              {/* Enemy overlays */}
              {overlayEnemies.map((e,i)=> (
                <div key={`enemy-${e.id}-${i}`} style={{
                  position:'absolute', left:`${e.x*100/MICRO_W}%`, top:`${e.y*100/MICRO_H}%`,
                  width:`${100/MICRO_W}%`, height:`${100/MICRO_H}%`, background:'rgba(239,68,68,0.85)', outline:'1px solid rgba(255,255,255,0.25)'
                }} title={`ENEMY ${e.id} @(${e.x},${e.y})`} />
              ))}
              {/* Safe zone (approximate circle) */}
              {overlaySafeZone?.center && typeof overlaySafeZone?.radius==='number' && (
                <div style={{
                  position:'absolute',
                  left: `${(overlaySafeZone.center.x - overlaySafeZone.radius) * 100 / MICRO_W}%`,
                  top: `${(overlaySafeZone.center.y - overlaySafeZone.radius) * 100 / MICRO_H}%`,
                  width: `${overlaySafeZone.radius * 2 * 100 / MICRO_W}%`,
                  height: `${overlaySafeZone.radius * 2 * 100 / MICRO_H}%`,
                  borderRadius: '50%',
                  border: '2px dashed rgba(59,130,246,0.9)'
                }} title={`SAFE ZONE r=${overlaySafeZone.radius}`} />
              )}
            </div>
          </div>
          <h3>Inventories</h3>
          {lastScores.length>0 && (
            <div style={{ marginBottom:8, fontSize:12 }}>
              <div style={{ color:'#6cf' }}>Top scored positions:</div>
              {lastScores.map((s,i)=>(<div key={i} style={{ fontFamily:'monospace' }}>{i+1}. ({s.x},{s.y}) score={s.score.toFixed(2)}</div>))}
            </div>
          )}
          {(decisionHL || decisionTL) && (
            <div style={{ marginBottom:8, maxHeight: 300, overflowY:'auto', border:'1px solid #333', padding:8 }}>
              <h3>Decision (HL/TL)</h3>
              {decisionHL && (
                <div style={{ background:'#111', color:'#eee', padding:8, marginBottom:6 }}>
                  <div style={{ color:'#6cf', marginBottom:4 }}>HL</div>
                  <pre style={{ whiteSpace:'pre-wrap', fontSize:12 }}>{JSON.stringify(decisionHL, null, 2)}</pre>
                </div>
              )}
              {decisionTL && (
                <div style={{ background:'#111', color:'#eee', padding:8 }}>
                  <div style={{ color:'#6cf', marginBottom:4 }}>TL</div>
                  <pre style={{ whiteSpace:'pre-wrap', fontSize:12 }}>{JSON.stringify(decisionTL, null, 2)}</pre>
                </div>
              )}
              {decisionActions && (
                <div style={{ background:'#111', color:'#eee', padding:8, marginTop:6 }}>
                  <div style={{ color:'#6cf', marginBottom:4 }}>Final Actions</div>
                  <pre style={{ whiteSpace:'pre-wrap', fontSize:12 }}>{JSON.stringify(decisionActions, null, 2)}</pre>
                </div>
              )}
            </div>
          )}
          {Object.keys(inventories).length === 0 ? (
            <div style={{ marginBottom:12, color:'#888' }}>— 点击 Refresh Inventories 查看背包</div>
          ) : (
            <div style={{ display:'grid', gap:8, gridTemplateColumns:'repeat(auto-fit, minmax(240px, 1fr))', marginBottom:12 }}>
              {Object.entries(inventories).map(([aid, inv]) => (
                <div key={aid} style={{ background:'#111', color:'#0f0', padding:8 }}>
                  <div style={{ color:'#6cf', marginBottom:6 }}>{aid}</div>
                  <div style={{ color:'#aaa' }}>Equipment:</div>
                  <div style={{ fontFamily:'monospace', fontSize:12, marginBottom:6 }}>
                    {Object.keys(inv.equipment).length===0 ? '—' : (
                      Object.entries(inv.equipment).map(([slot, item]: any) => (
                        <div key={slot}>{slot}: {item ? `${item.type}:${item.name} (${item.itemId})` : 'empty'}</div>
                      ))
                    )}
                  </div>
                  <div style={{ color:'#aaa' }}>Slots:</div>
                  <div style={{ fontFamily:'monospace', fontSize:12 }}>
                    {inv.slots.length===0 ? '—' : inv.slots.map((s, i) => (
                      <div key={i}>{s.item?.type}:{s.item?.name} x{s.quantity} ({s.item?.itemId})</div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          <h3>Logs</h3>
          <div style={{ height: 260, overflowY: 'auto', background:'#222', color:'#ddd', padding:8 }}>
            {logs.map((l,i)=>(<div key={i} style={{ fontFamily:'monospace' }}>{l}</div>))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SingleAgentMicroMapTest;
